import { useMemo, useState } from "react";
import { Box, Separator } from "@salestrak/ui";

import { AdvancedFilters } from "#app/components/filters/advanced-filters";
import {
  FilterModeHeader,
  type SavedFiltersTab,
} from "#app/components/filters/filter-mode-tabs";
import { QuickFilters } from "#app/components/filters/quick-filters";
import {
  SavedFilterNameDialog,
  SavedFiltersSection,
  savedFilterDialogKey,
  type SavedFilterDialogIntent,
  type SavedFiltersProps,
} from "#app/components/filters/saved-filters";
import { useIssueFilters } from "#app/lib/filters/issue-filter-context";
import type { IssueScope } from "#app/lib/filters/issue-scope";
import type { FilterConfig } from "#app/lib/filters/types";
import { useSavedFilters } from "#app/lib/hooks/use-saved-filters";
import { useViews } from "#app/lib/views/view-context";

import { OptionsPanelHeaderRung } from "./options-panel-slots";

export type ViewFiltersProps = {
  /** The merged filter config the issue screens publish — field defs only. */
  config: FilterConfig;
};

/**
 * The Customize view panel's Filters page: the VIEW'S filters — Simple and
 * Advanced — edited against the draft. The state is `IssueFilterProvider`'s
 * view layer (`viewFilters`), bound to the draft inside a saved-view scope,
 * so a value picked here previews on the view at once, lights the toolbar
 * chip and counts toward Save. The toolbar chip's own panel edits the OTHER
 * layer — the temporary session pool — which never lands here and never
 * counts toward Save. Nothing here seeds or reports — the provider does both.
 *
 * A page in the panel's own grammar, not the toolbar's hover menu: it is
 * reached by drilling into an `OptionPanel` list, so it renders as the same
 * kind of list it was opened from. `QuickFilters` is that list — the same rows
 * the mobile filter drawer shows, opening their values beside the panel here
 * and as a drilled page on a phone — and `AdvancedFilters` is the same block
 * builder the toolbar opens. Both editors
 * read the one options index, so a value set here and a value set from the
 * toolbar chip can never disagree about what a filter offers.
 *
 * The header rung carries the toolbar panel's two editor tabs — Simple and
 * Advanced — with its Save and Saved toggle at the end. Saved filters are
 * the viewer's personal snapshots, one store per category on every surface:
 * applying one REPLACES both editors, which lands in the view's draft like
 * any other edit, so a saved set is how a person seeds a view's filters from
 * one they already keep, and Save snapshots the draft as it stands. A value
 * comes off by unchecking it in its own list; there is no clear-all.
 */
export function ViewFilters({ config }: ViewFiltersProps) {
  const { viewFilters } = useIssueFilters();
  const { scope } = useViews();
  // A view saved on a workflow renders only that workflow's projects, so its
  // filters get the same scoping the toolbar panel gets.
  const issueScope = useMemo<IssueScope | undefined>(
    () =>
      scope.kind === "workflow"
        ? { kind: "workflow", workflowId: scope.id }
        : undefined,
    [scope],
  );

  // Saved is a PLACE on the rung, not an editor mode: selecting it leaves
  // both editors' filters applied, and picking Simple or Advanced sets the
  // mode and leaves Saved. The name dialog is keyed on the intent so each
  // open seeds its own name (a rename starts from the row's; a save blank).
  const savedFilterStore = useSavedFilters(config.category);
  const [savedTabOpen, setSavedTabOpen] = useState(false);
  const [dialogIntent, setDialogIntent] =
    useState<SavedFilterDialogIntent | null>(null);
  const savedTab = useMemo<SavedFiltersTab>(
    () => ({
      selected: savedTabOpen,
      count: savedFilterStore.savedFilters.length,
      onSelectedChange: setSavedTabOpen,
      onSaveRequest: () => setDialogIntent({ kind: "create" }),
    }),
    [savedTabOpen, savedFilterStore.savedFilters.length],
  );
  const savedFilters = useMemo<SavedFiltersProps>(
    () => ({
      store: savedFilterStore,
      onSaveRequest: () => setDialogIntent({ kind: "create" }),
      onRenameRequest: (row) => setDialogIntent({ kind: "rename", row }),
    }),
    [savedFilterStore],
  );

  // The page is reached from a saved view's panel, so the view layer is
  // bound; without one there is nothing to edit.
  if (!viewFilters) return null;
  const filters = viewFilters;

  return (
    <Box direction="column">
      {/* The sheet is non-modal and stays open across the dialog, so the
          dialog can live in this subtree — unlike the toolbar menu, which
          closes on the row press that asks for it. */}
      <SavedFilterNameDialog
        key={savedFilterDialogKey(dialogIntent)}
        intent={dialogIntent}
        onClose={() => setDialogIntent(null)}
        filters={filters}
        store={savedFilterStore}
        via="view_panel"
      />
      {/* The header rung — mode tabs, Save and the Saved toggle — is part
          of the panel's header block, the same rung the Fields page spends on
          its search: it rides `OptionsPanelHeaderRung` above the scrolling
          list, and the hairline under it is where the header ends and the
          list begins; the shell skips its own separator on this page for it.
          The same rung the toolbar menu opens on. */}
      <OptionsPanelHeaderRung>
        <Box paddingX="lg" paddingBottom="base">
          <FilterModeHeader filters={filters} saved={savedTab} />
        </Box>
        <Separator />
      </OptionsPanelHeaderRung>
      {/* An option-panel list, like the mobile drawer: rows open their
          values, and the row actions sit behind a trailing menu. */}
      {savedTabOpen ? (
        <SavedFiltersSection config={config} filters={filters} {...savedFilters} />
      ) : filters.activeMode === "simple" ? (
        <QuickFilters config={config} filters={filters} scope={issueScope} />
      ) : (
        <AdvancedFilters config={config} filters={filters} scope={issueScope} />
      )}
    </Box>
  );
}
